import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Hotkey } from 'ui-components/components/hotkey';

import { messages } from './undo-redo-hotkeys-messages';

import type React from 'react';

type UndoRedoHotkeysProps = {
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  children: React.ReactNode;
};

type InputBufferKey = {
  key: string;
  hasCtrlEquivalent: boolean;
  hasShift: boolean;
};

export const UndoRedoHotkeys = ({ onUndo, onRedo, canUndo, canRedo, children }: UndoRedoHotkeysProps) => {
  const { formatMessage } = useIntl();

  const undoHotkey = useMemo(
    () => ({
      hotkey: ['z'],
      handler: () => {
        if (canUndo) {
          onUndo();
        }
      },
      matches: (_str: string, inputBuffer: InputBufferKey[]) => {
        const curr = inputBuffer[inputBuffer.length - 1];
        return curr.key === 'z' && curr.hasCtrlEquivalent && !curr.hasShift;
      },
      name: formatMessage(messages.undo),
      doNotTrack: true,
    }),
    [onUndo, canUndo, formatMessage],
  );

  const redoHotkeyY = useMemo(
    () => ({
      hotkey: ['y'],
      handler: () => {
        if (canRedo) {
          onRedo();
        }
      },
      matches: (_str: string, inputBuffer: InputBufferKey[]) => {
        const curr = inputBuffer[inputBuffer.length - 1];
        return curr.key === 'y' && curr.hasCtrlEquivalent;
      },
      name: formatMessage(messages.redoCtrlY),
      doNotTrack: true,
    }),
    [onRedo, canRedo, formatMessage],
  );

  const redoHotkeyShiftZ = useMemo(
    () => ({
      hotkey: ['z'],
      handler: () => {
        if (canRedo) {
          onRedo();
        }
      },
      matches: (_str: string, inputBuffer: InputBufferKey[]) => {
        const curr = inputBuffer[inputBuffer.length - 1];
        return curr.key === 'z' && curr.hasCtrlEquivalent && curr.hasShift;
      },
      name: formatMessage(messages.redoCtrlShiftZ),
      doNotTrack: true,
    }),
    [onRedo, canRedo, formatMessage],
  );

  return (
    <>
      <Hotkey hotkey={undoHotkey} />
      <Hotkey hotkey={redoHotkeyY} />
      <Hotkey hotkey={redoHotkeyShiftZ} />
      {children}
    </>
  );
};
